import { DataGridColumn } from '@/components/dataGrid/entities/types';
import Tooltip from '@/components/tooltip';
import { LEAD_INDICATION_DOWNLOAD_LABEL } from '@/modules/leadIndication/entities/constants';
import {
  LEAD_INDICATION_LIST_FIELDS,
  LeadIndication,
  LeadIndicationListField,
  LeadIndicationListParams,
} from '@/modules/leadIndication/entities/leadIndication';
import ICONS from '@/shared/entities/constants/icons';
import { useMemo } from 'react';
import { Button } from 'vkit/lib/components';

interface UseLeadIndicationColumnsOptions {
  visibleFields?: LeadIndicationListField[];
  loadingFileId?: string | null;
  onDownload: (indication: LeadIndication) => void;
}

const useLeadIndicationColumns = ({
  visibleFields,
  loadingFileId,
  onDownload,
}: UseLeadIndicationColumnsOptions) => {
  const columnsByField = useMemo<
    Record<LeadIndicationListField, DataGridColumn<LeadIndication, LeadIndicationListParams>>
  >(
    () => ({
      referringCompany: {
        headerName: 'Empresa indicadora',
        field: 'referringCompany',
        sortable: true,
        minWidth: '220px',
      },
      cnpj: {
        headerName: 'CNPJ',
        field: 'cnpj',
        formatData: 'cnpj',
        sortable: true,
        minWidth: '170px',
      },
      indicatedAt: {
        headerName: 'Indicado em',
        field: 'indicatedAt',
        formatData: 'dateTime',
        sortable: true,
        minWidth: '180px',
      },
      indicatedBy: {
        headerName: 'Indicado por',
        field: 'indicatedBy',
        sortable: true,
        minWidth: '180px',
      },
      actions: {
        headerName: 'Ações',
        align: 'right',
        minWidth: '220px',
        render: (indication) => (
          <Tooltip title={LEAD_INDICATION_DOWNLOAD_LABEL} align='right'>
            <span
              title={LEAD_INDICATION_DOWNLOAD_LABEL}
              aria-label={LEAD_INDICATION_DOWNLOAD_LABEL}>
              <Button
                solo
                outlined
                label='Download'
                icon={ICONS.DOWNLOAD}
                disabled={!indication.fileId || loadingFileId === indication.fileId}
                loading={Boolean(indication.fileId) && loadingFileId === indication.fileId}
                onClick={() => onDownload(indication)}
              />
            </span>
          </Tooltip>
        ),
      },
    }),
    [loadingFileId, onDownload],
  );

  const columns = useMemo(() => {
    const fields = visibleFields
      ? LEAD_INDICATION_LIST_FIELDS.filter((field) => visibleFields.includes(field))
      : LEAD_INDICATION_LIST_FIELDS;

    return fields.map((field) => columnsByField[field]);
  }, [columnsByField, visibleFields]);

  return { columns };
};

export default useLeadIndicationColumns;
